import { useState } from 'react';
import { useData, useSession } from '@yingbaba/api';
import {
  Select,
  ActionDialog,
  ErrorNotice,
  PageTitle,
  QueryState,
  Status,
  Notice,
} from '@yingbaba/ui';
import { datetime, money } from '@yingbaba/utils';

type RefundRequest = {
  id: string;
  project_id: string;
  payment_id: string;
  cancellation_request_id: string;
  requested_by_organization_id: string;
  counterparty_organization_id: string;
  amount_minor: number;
  currency: string;
  reason: string;
  response_reason: string;
  finance_reason: string;
  refund_id: string;
  status: string;
  created_at: string;
};
type Dispute = {
  id: string;
  project_id: string;
  raised_by_organization_id: string;
  counterparty_organization_id: string;
  category: string;
  related_type: string;
  related_id: string;
  title: string;
  description: string;
  counterparty_statement: string;
  status: string;
  resolution: string;
  resolution_action: string;
  created_at: string;
};

const refundStatuses = [
  ['', '全部退款申请'],
  ['pending_finance', '待财务处理'],
  ['processing', '退款处理中'],
  ['succeeded', '已退款'],
  ['rejected', '已拒绝'],
] as const;
const disputeStatuses = [
  ['', '全部争议'],
  ['open', '待对方回应'],
  ['responded', '待平台处理'],
  ['resolved', '已处理'],
] as const;

export function AfterSalesWorkbench() {
  const session = useSession();
  const role = session.data?.account.platform_role || '';
  const finance = role === 'super_admin' || role === 'finance';
  const canResolve = finance || role === 'customer_service';
  const [refundStatus, setRefundStatus] = useState('pending_finance');
  const [disputeStatus, setDisputeStatus] = useState('');
  const refunds = useData<RefundRequest[]>(
    `/admin/refund-requests${refundStatus ? `?status=${refundStatus}` : ''}`,
  );
  const disputes = useData<Dispute[]>(
    `/admin/disputes${disputeStatus ? `?status=${disputeStatus}` : ''}`,
  );
  return (
    <>
      <PageTitle
        title="售后与争议"
        description="集中处理双方已经协商过的退款申请和升级平台的争议。真实退款继续复用支付渠道退款、分账回退和财务对账流程。"
      />
      <ErrorNotice error={refunds.error || disputes.error} />
      <div className="grid-2" style={{ alignItems: 'start' }}>
        <section className="panel stack">
          <div className="panel-title">
            <div>
              <h3>退款申请</h3>
              <p className="small muted">对方同意后进入财务审核；批准会创建正式 Refund 记录。</p>
            </div>
            <Select
              aria-label="退款申请状态"
              value={refundStatus}
              onChange={(e) => setRefundStatus(e)}
            >
              {refundStatuses.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <QueryState query={refunds} empty={refunds.data?.length === 0}>
            {(refunds.data || []).map((item) => (
              <div className="list-item" key={item.id}>
                <div className="grow">
                  <div className="row">
                    <strong>{money(item.amount_minor)}</strong>
                    <Status value={item.status} />
                    <span className="small muted">{datetime(item.created_at)}</span>
                  </div>
                  <p>{item.reason}</p>
                  <p className="small muted">
                    项目 {item.project_id.slice(-8)} · 付款 {item.payment_id.slice(-8)}
                  </p>
                  {item.response_reason && (
                    <p className="small muted">协商说明：{item.response_reason}</p>
                  )}
                  {item.finance_reason && (
                    <p className="small muted">财务说明：{item.finance_reason}</p>
                  )}
                  {item.refund_id && (
                    <p className="small muted">退款记录：{item.refund_id.slice(-8)}</p>
                  )}
                </div>
                {finance && item.status === 'pending_finance' && (
                  <div className="row wrap">
                    <ActionDialog
                      title="批准退款申请"
                      trigger="批准"
                      intent="financial"
                      submitLabel="批准并创建退款"
                      path={`/admin/refund-requests/${item.id}/approve`}
                      description="批准后会按现有退款状态机创建正式退款，并在必要时先执行分账回退。"
                      fields={[
                        {
                          name: 'reason',
                          label: '财务说明',
                          type: 'textarea',
                          required: false,
                          max: 1000,
                        },
                      ]}
                    />
                    <ActionDialog
                      title="拒绝退款申请"
                      trigger="拒绝"
                      variant="secondary"
                      path={`/admin/refund-requests/${item.id}/reject`}
                      fields={[
                        { name: 'reason', label: '拒绝原因', type: 'textarea', min: 2, max: 1000 },
                      ]}
                    />
                  </div>
                )}
              </div>
            ))}
          </QueryState>
          {!finance && (
            <Notice>
              客服与审计角色可以查看退款申请，但只有财务 / 超级管理员可以批准或拒绝资金退款。
            </Notice>
          )}
        </section>

        <section className="panel stack">
          <div className="panel-title">
            <div>
              <h3>项目争议</h3>
              <p className="small muted">
                保留双方陈述和平台处理结论；关联取消/退款时可直接裁决关联事项。
              </p>
            </div>
            <Select
              aria-label="争议状态"
              value={disputeStatus}
              onChange={(e) => setDisputeStatus(e)}
            >
              {disputeStatuses.map(([value, label]) => (
                <option value={value} key={value}>
                  {label}
                </option>
              ))}
            </Select>
          </div>
          <QueryState query={disputes} empty={disputes.data?.length === 0}>
            {(disputes.data || []).map((item) => (
              <div className="list-item" key={item.id}>
                <div className="grow">
                  <div className="row">
                    <Status value={item.status} />
                    <strong>{item.title}</strong>
                    <span className="small muted">{datetime(item.created_at)}</span>
                  </div>
                  <p>{item.description}</p>
                  {item.counterparty_statement && (
                    <Notice>对方陈述：{item.counterparty_statement}</Notice>
                  )}
                  {item.resolution && <Notice>处理结论：{item.resolution}</Notice>}
                  <p className="small muted">
                    项目 {item.project_id.slice(-8)}
                    {item.related_type
                      ? ` · 关联 ${item.related_type} ${item.related_id.slice(-8)}`
                      : ''}
                  </p>
                </div>
                {canResolve && !['resolved', 'closed'].includes(item.status) && (
                  <ActionDialog
                    title="处理项目争议"
                    trigger="处理"
                    path={`/admin/disputes/${item.id}/resolve`}
                    fields={[
                      {
                        name: 'action',
                        label: '处理动作',
                        options: ['cancellation_request', 'refund_request'].includes(
                          item.related_type,
                        )
                          ? [
                              { value: 'record', label: '仅记录处理结论' },
                              { value: 'approve_related', label: '支持申请方并批准关联事项' },
                              { value: 'reject_related', label: '不支持申请方并拒绝关联事项' },
                            ]
                          : [{ value: 'record', label: '记录处理结论' }],
                      },
                      {
                        name: 'resolution',
                        label: '平台处理结论',
                        type: 'textarea',
                        min: 2,
                        max: 5000,
                      },
                    ]}
                  />
                )}
              </div>
            ))}
          </QueryState>
        </section>
      </div>
    </>
  );
}
